/**
 * source-models-panels.tsx — Bước 2 của màn Nguồn AI và lượt AI: "Chọn model của tổ chức" theo nguồn đang xem.
 *
 *  - Lưới THẺ model (components/ai/model-card-grid.tsx — xếp hàng ngang, tự xuống dòng) từ `GET /ai/models?source=`:
 *    AI của eCDP chỉ có nền tảng / model eCDP đang bật; Key của tôi đủ mọi nền tảng. Thẻ đang dùng: "Đang dùng".
 *  - Bấm một thẻ → khung "Cài đặt model <tên>" ngay dưới lưới (KHÔNG đổi ngay):
 *      AI của eCDP: mô tả, credit/lượt (+ số dư đủ ~bao nhiêu lượt), khoá theo gói → "Nâng gói";
 *      Key của tôi: API key CỦA ĐÚNG nhà cung cấp model đó (trạng thái, nhập / Lưu key — `PUT /ai/api-key` tự kiểm tra,
 *      Cập nhật / Xoá).
 *    Nút "Dùng model này" (`PUT /ai/config {provider, model}`) — Key của tôi chỉ bật khi nhà cung cấp đã có key hợp lệ; tổ
 *    chức đang dùng nguồn khác → hỏi rồi chuyển cả tổ chức sang nguồn đang xem trước (`PUT /ai/sources`).
 *  - Khối gập "Key đã lưu": key của các nhà cung cấp đã lưu từ trước (xem / cập nhật / xoá — không mất key cũ) + key nền
 *    tảng đã ngừng hỗ trợ (chỉ xoá).
 *    "Model tuỳ chỉnh" TẠM ẨN theo chủ sản phẩm.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { Check, ChevronDown, KeyRound, Loader2, Save, Settings2, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { AiProviderOption, LegacyProviderWarning } from '@/components/shared/ai-provider-option'
import { OpenInNewTab } from '@/components/ai/open-in-new-tab'
import { ModelCardGrid } from '@/components/ai/model-card-grid'
import { lockedByPlan, lockLabel, modelDecision, providerLabel, sameModel, TIER_DESC, tierLabel, type ModelRef } from '@/components/ai/model-card-grid-lib'
import { aiKeys, deleteApiKey, saveApiKey, updateAiConfig, type AiConfig } from '@/hooks/use-ai'
import { aiCreditKeys, invalidateAiSourceData, useAiModelsBySource, useUpdateAiSources } from '@/hooks/use-ai-credits'
import {
  aiErrorMessage, formatTurns, perReplyText, SOURCE_LABEL, turnsPerReply, walletHeadline,
  type AiCredits, type AiModelEntry, type AiProviderSource, type AiSource, type AiSources,
} from '@/lib/ai-credits'
import { currentLocale } from '@/lib/i18n'
import { legacyKeyProviders, legacyProviderName } from '@/lib/ai-providers'
import { cn } from '@/lib/utils'
import { ToneBadge } from '../credits/credit-widgets'
import { keyStatus, needsSourceSwitch, orgModelInView, unifySourcesBody, type SourceMode } from './source-mode-lib'

const UPGRADE_PATH = '/billing/upgrade'

type Names = Array<{ provider: string; name: string }>

export function OrgModelStep({ config, view, mode, sources, credits, names, canEdit, onPickByok }: {
  config: AiConfig
  view: AiSource
  mode: SourceMode
  sources: AiSources | null | undefined
  credits: AiCredits | null | undefined
  names: Names
  canEdit: boolean
  onPickByok: () => void
}) {
  const { t } = useTranslation('aiOps')
  const qc = useQueryClient()
  const q = useAiModelsBySource(view)
  const updateSources = useUpdateAiSources()
  const providers = sources?.providers ?? []
  const models = (q.data?.models ?? []).filter((m) => m.source === view)
  const current = orgModelInView(config, providers, view)
  const [picked, setPicked] = useState<ModelRef | null>(null)
  const [saving, setSaving] = useState(false)
  // Chưa bấm thẻ nào → mở sẵn khung của model đang dùng (nếu có trong danh sách).
  const selectedRef = picked ?? (current && models.some((m) => sameModel(m, current)) ? current : null)
  const selected = models.find((m) => sameModel(m, selectedRef)) ?? null
  const srcOf = (provider: string) => providers.find((p) => p.provider === provider) ?? null

  async function applyModel(m: AiModelEntry) {
    const switching = needsSourceSwitch(mode, view)
    if (switching && !window.confirm(
      t('sourceModels.confirmSwitch', { source: SOURCE_LABEL[view], title: m.title }),
    )) return
    setSaving(true)
    try {
      if (switching) await updateSources.mutateAsync(unifySourcesBody(providers, view))
      await updateAiConfig({ provider: m.provider, model: m.model })
      await Promise.all([
        qc.invalidateQueries({ queryKey: aiKeys.config }),
        qc.invalidateQueries({ queryKey: aiCreditKeys.models }),
        qc.invalidateQueries({ queryKey: aiKeys.bots }),
      ])
      setPicked(null)
      toast.success(switching ? t('sourceModels.orgUsesSwitched', { title: m.title, source: SOURCE_LABEL[view] }) : t('sourceModels.orgUses', { title: m.title }))
    } catch (err) {
      toast.error(aiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  if (view === 'platform' && (sources?.orgBlocked || sources?.platformEnabled === false)) {
    return (
      <EmptyBox>
        <Trans
          t={t} i18nKey="sourceModels.platformPaused"
          components={{ btn: <button type="button" className="text-primary hover:underline" onClick={onPickByok} /> }}
        />
      </EmptyBox>
    )
  }
  if (q.isLoading) return <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('sourceModels.loading')}</p>
  if (q.isError) return <EmptyBox>{t('sourceModels.loadError', { error: aiErrorMessage(q.error) })}</EmptyBox>
  if (models.length === 0) {
    return view === 'platform' ? (
      <EmptyBox>
        <Trans
          t={t} i18nKey="sourceModels.noPlatformModels"
          components={{ btn: <button type="button" className="font-medium text-primary hover:underline" onClick={onPickByok} /> }}
        />
      </EmptyBox>
    ) : <EmptyBox>{t('sourceModels.noModels')}</EmptyBox>
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {view === 'platform'
          ? <>{t('sourceModels.platformIntro')}{credits ? <> <Trans t={t} i18nKey="sourceModels.platformLeft" values={{ amount: walletHeadline(credits).main }} components={{ b: <b className="tabular-nums text-foreground" /> }} /></> : null}{q.data?.plan ? <> <Trans t={t} i18nKey="sourceModels.platformPlan" values={{ name: q.data.plan.name }} components={{ b: <b className="text-foreground" /> }} /></> : null}.</>
          : t('sourceModels.byokIntro')}
        {' '}{t('sourceModels.clickModel')}
      </p>
      <ModelCardGrid
        models={models} current={current} selected={selectedRef} onSelect={(m) => setPicked({ provider: m.provider, model: m.model })}
        byokOf={(p) => srcOf(p)?.byok} names={names} ariaLabel={t('sourceModels.gridAria', { source: SOURCE_LABEL[view] })}
      />
      {view === 'platform' && <p className="text-xs text-muted-foreground">{t('sourceModels.platformNote')}</p>}

      {selected && (
        <ModelSettings
          key={`${selected.provider}:${selected.model}`}
          m={selected} current={current} src={srcOf(selected.provider)} names={names} credits={credits}
          canEdit={canEdit} saving={saving} switching={needsSourceSwitch(mode, view)} view={view}
          scrollOnShow={!!picked} onUse={() => void applyModel(selected)}
        />
      )}

      <SavedKeys config={config} sources={sources} canEdit={canEdit} exclude={view === 'byok' ? selected?.provider : undefined} />
    </div>
  )
}

/** Khung "Cài đặt model <tên>" dưới lưới. */
function ModelSettings({ m, current, src, names, credits, canEdit, saving, switching, view, scrollOnShow, onUse }: {
  m: AiModelEntry
  current: ModelRef | null
  src: AiProviderSource | null
  names: Names
  credits: AiCredits | null | undefined
  canEdit: boolean
  saving: boolean
  switching: boolean
  view: AiSource
  /** Người dùng vừa bấm thẻ → cuộn khung vào tầm nhìn (lưới dài, khung nằm dưới cùng). */
  scrollOnShow: boolean
  onUse: () => void
}) {
  const { t } = useTranslation('aiOps')
  const ref = useRef<HTMLElement>(null)
  useEffect(() => {
    if (scrollOnShow) ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
  }, [scrollOnShow])
  const name = providerLabel(m.provider, names)
  const d = modelDecision({ model: m, byok: src?.byok, current, providerName: name })
  const lock = lockLabel(m)
  const tier = tierLabel(m.tier)

  return (
    <section ref={ref} aria-label={t('sourceModels.settingsTitle', { title: m.title })} className="space-y-3 rounded-xl border-2 border-primary/30 bg-primary/5 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Settings2 className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold">{t('sourceModels.settingsTitle', { title: m.title })}</h3>
        <span className="text-xs text-muted-foreground"><AiProviderOption id={m.provider} name={name} /></span>
        {d.isCurrent && <Badge variant="success" className="gap-1 text-[10px]"><Check className="h-3 w-3" /> {t('shared.inUse')}</Badge>}
      </div>
      <p className="text-sm text-muted-foreground">
        {tier ? <><b className="text-foreground">{tier}.</b> {m.tier ? TIER_DESC[m.tier] ?? '' : ''}</> : null}
        {' '}<span className="font-mono text-xs">{m.model}</span>
      </p>

      {m.source === 'platform' ? (
        <div className="space-y-1 text-sm">
          <p>
            {perReplyText(m) ? <Trans t={t} i18nKey="sourceModels.perReply" values={{ amount: perReplyText(m)!.replace(/ \/ .*$/, '') }} components={{ b: <b className="tabular-nums" /> }} /> : t('sourceModels.perReplyDefault')}
          </p>
          {credits && turnsPerReply(m) ? (
            <p className="text-muted-foreground">
              <Trans
                t={t} i18nKey="sourceModels.enoughFor"
                values={{ amount: walletHeadline(credits).main, turns: formatTurns(Math.floor(walletHeadline(credits).turns / turnsPerReply(m)!), '') }}
                components={{ b: <b className="tabular-nums text-foreground" /> }}
              />
            </p>
          ) : null}
          {lock && (
            <p className="flex flex-wrap items-center gap-2 text-sm">
              <ToneBadge tone="warning">🔒 {lock}</ToneBadge>
              {lockedByPlan(m) && (
                <OpenInNewTab to={UPGRADE_PATH} title={t('sourceModels.upgradeTitle')}>{t('sourceModels.upgrade')}</OpenInNewTab>
              )}
            </p>
          )}
        </div>
      ) : (
        <ProviderKeyBox provider={m.provider} name={name} src={src} canEdit={canEdit} />
      )}

      <div className="flex flex-wrap items-center gap-3 border-t pt-3">
        {canEdit && (
          <Button onClick={onUse} disabled={!d.enabled || saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t('sourceModels.useModel')}
          </Button>
        )}
        <span className="min-w-0 flex-1 text-xs text-muted-foreground">
          {d.reason && !d.isCurrent ? d.reason : d.isCurrent ? t('sourceModels.hintCurrent')
            : switching ? t('sourceModels.hintSwitching', { source: SOURCE_LABEL[view] })
              : t('sourceModels.hintApply')}
        </span>
      </div>
    </section>
  )
}

/** API key của MỘT nhà cung cấp: trạng thái, nhập / Lưu key (máy chủ kiểm tra trước khi lưu), Cập nhật, Xoá. */
export function ProviderKeyBox({ provider, name, src, canEdit, compact = false }: {
  provider: string
  name: string
  src: AiProviderSource | null
  canEdit: boolean
  compact?: boolean
}) {
  const { t } = useTranslation('aiOps')
  const qc = useQueryClient()
  const st = keyStatus(src?.byok)
  const configured = !!src?.byok.configured
  const [editing, setEditing] = useState(false)
  const [key, setKey] = useState('')
  const [busy, setBusy] = useState(false)
  // Không có quyền sửa (`ai.update`) → chỉ xem trạng thái key, không hiện ô nhập / nút.
  const showInput = canEdit && (!configured || editing)
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: aiKeys.config }), invalidateAiSourceData(qc)])

  async function save() {
    if (!key.trim()) return
    setBusy(true)
    try {
      const res = await saveApiKey(provider, key.trim())
      await refresh()
      setKey(''); setEditing(false)
      toast.success(res.verified ? t('sourceModels.keyVerifiedSaved', { name }) : t('sourceModels.keySaved', { name }))
    } catch (err) {
      toast.error(aiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!window.confirm(t('sourceModels.confirmDeleteKey', { name }))) return
    setBusy(true)
    try {
      await deleteApiKey(provider)
      await refresh()
      toast.success(t('sourceModels.keyDeletedNamed', { name }))
    } catch (err) {
      toast.error(aiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={cn('space-y-2 rounded-lg border bg-card', compact ? 'p-2.5' : 'p-3', st.tone === 'destructive' && 'border-destructive/40')}>
      <div className="flex flex-wrap items-center gap-2">
        <KeyRound className="h-4 w-4 text-muted-foreground" />
        <span className="min-w-0 flex-1 text-sm font-medium"><Trans t={t} i18nKey="sourceModels.keyOf" components={{ provider: <AiProviderOption id={provider} name={name} /> }} /></span>
        <ToneBadge tone={st.tone}>{st.label}</ToneBadge>
        {canEdit && configured && !editing && (
          <>
            <Button variant="outline" size="sm" disabled={busy} onClick={() => setEditing(true)}>{t('common:actions.update')}</Button>
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => void remove()} aria-label={t('sourceModels.deleteKeyAria', { name })}>
              <Trash2 className="h-3.5 w-3.5 text-destructive" /> {t('common:actions.delete')}
            </Button>
          </>
        )}
      </div>
      {st.error && <p className="text-xs text-destructive">{t('sourceModels.lastError', { error: st.error })}</p>}
      {configured && !st.error && src?.byok.lastVerifiedAt && !editing && (
        <p className="text-xs text-muted-foreground">{t('sourceModels.checkedAt', { time: new Date(src.byok.lastVerifiedAt).toLocaleString(currentLocale()) })}</p>
      )}
      {showInput && (
        <div className="flex flex-wrap gap-2">
          <Input
            type="password" className="min-w-[200px] flex-1" placeholder={t('sourceModels.pasteKey', { name })} value={key}
            onChange={(e) => setKey(e.target.value)} disabled={busy} aria-label={t('sourceModels.keyAria', { name })}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void save() } }}
          />
          <Button onClick={() => void save()} disabled={busy || !key.trim()}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {busy ? t('sourceModels.checking') : t('sourceModels.saveKey')}
          </Button>
          {editing && <Button variant="ghost" onClick={() => { setEditing(false); setKey('') }}>{t('common:actions.cancel')}</Button>}
        </div>
      )}
      {showInput && !compact && <p className="text-xs text-muted-foreground">{t('sourceModels.keyNote')}</p>}
    </div>
  )
}

/** Khối gập "Key đã lưu": key của các nhà cung cấp đã lưu (trừ nhà cung cấp đang mở ở khung Cài đặt) + key nền tảng cũ. */
function SavedKeys({ config, sources, canEdit, exclude }: {
  config: AiConfig
  sources: AiSources | null | undefined
  canEdit: boolean
  exclude?: string
}) {
  const { t } = useTranslation('aiOps')
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const saved = (sources?.providers ?? []).filter((p) => p.byok.configured && p.provider !== exclude)
  const legacy = legacyKeyProviders(config.providerKeys)
  if (!saved.length && !legacy.length) return null

  async function removeLegacy(id: string) {
    if (!window.confirm(t('sourceModels.confirmDeleteLegacy', { name: legacyProviderName(id) }))) return
    setBusy(true)
    try {
      await deleteApiKey(id)
      await Promise.all([qc.invalidateQueries({ queryKey: aiKeys.config }), invalidateAiSourceData(qc)])
      toast.success(t('sourceModels.keyDeleted'))
    } catch (err) {
      toast.error(aiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-md border">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground hover:bg-muted/40">
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
        {t('sourceModels.savedKeys', { count: saved.length + legacy.length })}
      </button>
      {open && (
        <div className="space-y-2 border-t p-3">
          {saved.map((p) => <ProviderKeyBox key={p.provider} provider={p.provider} name={p.name} src={p} canEdit={canEdit} compact />)}
          {legacy.map((id) => (
            <div key={id} className="flex items-center justify-between gap-2 rounded-lg border border-warning/40 p-2.5">
              <div className="space-y-0.5">
                <span className="text-sm">{legacyProviderName(id)} · <Badge variant="outline">{t('sourceModels.hasKey')}</Badge></span>
                <LegacyProviderWarning provider={id} />
              </div>
              {canEdit && (
                <Button variant="ghost" size="icon" disabled={busy} onClick={() => void removeLegacy(id)} aria-label={t('sourceModels.deleteKeyAria', { name: legacyProviderName(id) })} title={t('sourceModels.deleteKey')}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function EmptyBox({ children }: { children: ReactNode }) {
  return <div className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">{children}</div>
}
